import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const SITE_URL = "https://juristube.com.br";
const DEFAULT_IMAGE = `${SITE_URL}/brand/juristube-banner-1200x471.png`;

/* --------------------------------------------------------------------- */
/*  Traduções da descrição sitewide (Iter189).                           */
/*                                                                       */
/*  Emitidas como `<meta name="description" lang="X">` sempre que a      */
/*  página usar o SITE_PITCH padrão — dão ao Google uma dica localizada  */
/*  para exibir na SERP internacional, sem duplicar descrição em         */
/*  páginas que já têm seu próprio texto customizado (radar, autor,      */
/*  episódio, etc). O idioma primário (pt-BR) já é servido pela          */
/*  `<meta name="description">` sem `lang`.                              */
/*                                                                       */
/*  As frases traduzidas seguem a mesma estrutura da versão em           */
/*  português — "infraestrutura viva de aprendizado e pesquisa em        */
/*  Direito Público" + lista de recursos + "em um só lugar" — para       */
/*  manter o pitch consistente em todos os idiomas.                      */
/* --------------------------------------------------------------------- */
const SITE_PITCH_TRANSLATIONS = {
  en:
    "JurisTube: a living infrastructure for learning and research in Brazilian Public Law. Podcasts, articles, statutes, news, videos, interactive tools and scientific curation in a single place.",
  es:
    "JurisTube: infraestructura viva de aprendizaje e investigación en Derecho Público. Podcasts, artículos, normas, noticias, vídeos, herramientas interactivas y curaduría científica en un solo lugar.",
  it:
    "JurisTube: infrastruttura viva di apprendimento e ricerca nel Diritto Pubblico. Podcast, articoli, norme, notizie, video, strumenti interattivi e curatela scientifica in un unico luogo.",
  fr:
    "JurisTube : infrastructure vivante d'apprentissage et de recherche en Droit Public brésilien. Podcasts, articles, textes normatifs, actualités, vidéos, outils interactifs et curation scientifique en un seul lieu.",
  "zh-CN":
    "JurisTube:巴西公法学习与研究的鲜活基础设施。播客、文章、法规、新闻、视频、互动工具和科学策展,一站汇聚。",
  de:
    "JurisTube: lebendige Infrastruktur für Lernen und Forschung im öffentlichen Recht Brasiliens. Podcasts, Artikel, Rechtsvorschriften, Nachrichten, Videos, interaktive Werkzeuge und wissenschaftliche Kuratierung — alles an einem Ort.",
  ru:
    "JurisTube — живая инфраструктура для обучения и исследований в бразильском публичном праве. Подкасты, статьи, нормативные акты, новости, видео, интерактивные инструменты и научная курация в едином пространстве.",
};
const SITE_NAME = "JurisTube";

/**
 * Unified SEO component — injects <title>, description, Open Graph and Twitter Card
 * meta tags for a page. Pass only what you want to override; sensible defaults are used.
 *
 * Usage:
 *   <SEO
 *     title="Episódio X — JurisTube"
 *     description="Resumo do episódio..."
 *     image={episode.thumbnail}
 *     url={`/episodio/${episode.id}`}
 *     type="video.other"
 *   />
 */
export default function SEO({
  title,
  description,
  image,
  // Iter298 — dimensões da imagem OG. Google Discover / Google News exigem
  // og:image:width e og:image:height quando queremos que o card grande
  // (1200 × 630) apareça. Se caller não passa, presumimos 1200×630 (o
  // tamanho recomendado que anunciamos no admin do blog).
  imageWidth,
  imageHeight,
  imageAlt,
  url,
  type = "website",
  keywords,
  noindex = false,
  // When ``noindexFollow`` is true, emit ``noindex,follow`` instead of
  // ``noindex,nofollow``. Use on pages that are themselves duplicates
  // (e.g. ``/divulgar``) but whose outbound links should still flow
  // PageRank to the canonical author/article pages.
  noindexFollow = false,
  // Out/2026 — autor real do item (<meta name="author">). Sem ele a
  // tag estática "Prof. Paulo Modesto · IBDP" do index.html permanece.
  author,
}) {
  // Formato único de título (Out/2026): "<título> | JurisTube". Qualquer
  // sufixo "— JurisTube" já presente no título recebido é removido para
  // nunca gerar "— JurisTube — JurisTube".
  const cleanTitle = (title || "").replace(/(\s*[—|\-–]\s*JurisTube(?:\.com\.br)?\s*)+$/i, "").trim();
  const fullTitle = cleanTitle ? `${cleanTitle} | ${SITE_NAME}` : `${SITE_NAME} — Direito Público em Vídeo, Áudio, Doutrina e Pesquisa`;
  // Build a description that's BOTH long enough for Bing's "too short"
  // check (≥110 chars) and shorter than Google's truncation point (≤160).
  // When the caller hands us nothing or something tiny, we pad with a
  // brief sitewide pitch so every page lands in the sweet spot.
  //
  // Iter190 (Fev/2026) — atualizado pelo editor após o site ter
  // crescido bem além dos "podcasts + palestras" iniciais. Descrição
  // acompanha o footer para manter coerência entre o `<meta>` do
  // Google e o texto visível na base do site.
  const SITE_PITCH = "JurisTube: infraestrutura viva de aprendizado e pesquisa em Direito Público. Podcasts, artigos, normas, notícias, vídeos, ferramentas interativas e curadoria científica em um só lugar.";
  // Default keyword list — Google effectively ignores this, but Bing and
  // Yandex still use it as one of several relevance signals, and it
  // serves as a human-readable manifest of the terms we want to be
  // discoverable for. Caller-supplied keywords (per-page) merge with
  // these defaults.
  const DEFAULT_KEYWORDS = [
    "direito público",
    "direito administrativo",
    "direito constitucional",
    "podcast jurídico",
    "videoteca de direito",
    "aulas de direito",
    "palestras jurídicas",
    "improbidade administrativa",
    "licitações e contratos",
    "agências reguladoras",
    "controle de constitucionalidade",
    "doutrina jurídica",
    "Conjur",
    "JOTA",
  ];
  const rawDesc = (description || "").replace(/\s+/g, " ").trim();
  let cleanDesc = rawDesc;
  if (!cleanDesc) {
    cleanDesc = SITE_PITCH;
  } else if (cleanDesc.length < 110) {
    // Append a separator + pitch fragment so we cross 110 chars but
    // don't end mid-word; cap at 158 with a trailing ellipsis only if
    // we actually truncated.
    cleanDesc = `${cleanDesc} · ${SITE_PITCH}`.slice(0, 158);
  } else if (cleanDesc.length > 160) {
    // Cut at the last full word before 158 chars and add an ellipsis.
    const truncated = cleanDesc.slice(0, 158).replace(/\s+\S*$/, "");
    cleanDesc = `${truncated}…`;
  }
  // Allow relative paths for url and image, resolve to absolute for social crawlers.
  const fullUrl = url ? (url.startsWith("http") ? url : `${SITE_URL}${url}`) : SITE_URL;
  // Iter271 — Rejeita data URIs, blob URLs e outras strings inválidas.
  // Google Search Console reportou "URL do campo image não é válido"
  // porque avatares base64 (data:image/jpeg;base64,...) estavam vazando
  // como og:image (viravam https://.../data:image/jpeg;base64,...).
  const isValidImageInput = (
    image
    && typeof image === "string"
    && !image.startsWith("data:")
    && !image.startsWith("blob:")
    && (image.startsWith("http") || image.startsWith("/"))
  );
  const fullImage = isValidImageInput
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : DEFAULT_IMAGE;
  const kwArray = Array.isArray(keywords) ? keywords : (keywords ? [keywords] : []);
  const mergedKeywords = [...new Set([...kwArray, ...DEFAULT_KEYWORDS])].slice(0, 20).join(", ");

  // Quando a rota declara noindex=true, removemos imperativamente
  // qualquer <meta name="robots"> "index,follow..." que tenha vindo do
  // index.html estático. O Helmet emite a sua própria tag noindex, mas
  // não consegue de-duplicar tags que ele não criou. Esperamos o
  // Helmet flushar suas tags (dois rAFs) e só então removemos as tags
  // robots SEM o atributo ``data-rh``.
  useEffect(() => {
    if (!noindex) return undefined;
    let removed = [];
    const rafs = [];
    rafs.push(requestAnimationFrame(() => {
      rafs.push(requestAnimationFrame(() => {
        document.head.querySelectorAll('meta[name="robots"]').forEach((tag) => {
          if (tag.hasAttribute("data-rh")) return;          // mantém a do Helmet
          if (tag.getAttribute("content")?.includes("noindex")) return;  // mantém qualquer noindex que já esteja lá
          removed.push({ parent: tag.parentNode, node: tag, next: tag.nextSibling });
          tag.parentNode?.removeChild(tag);
        });
      }));
    }));
    return () => {
      rafs.forEach((id) => cancelAnimationFrame(id));
      removed.forEach(({ parent, node, next }) => {
        try { parent?.insertBefore(node, next); } catch { /* noop */ }
      });
    };
  }, [noindex]);

  // Out/2026 — Dedupe SEGURA das meta tags de imagem estáticas
  // do ``index.html``. Iter176: react-helmet-async 1.x NÃO emite o
  // atributo ``data-rh`` (só o 2.x adiciona). Trocamos a heurística:
  // usamos POSIÇÃO no DOM — as tags do index.html estão SEMPRE ANTES
  // das do Helmet no <head>, porque Helmet apenas append no unmount.
  // Regra: quando há N>1 tags iguais, mantemos a última (Helmet) e
  // neutralizamos as anteriores via ``setAttribute('content', '')``.
  // ``setAttribute`` NÃO altera a árvore do DOM → zero interação com
  // React fiber tree → zero crash de ``removeChild``.
  useEffect(() => {
    const neutralized = []; // {tag, originalContent}
    let cancelled = false;

    const neutralize = () => {
      const selectors = [
        'meta[property="og:image"]',
        'meta[property="og:image:width"]',
        'meta[property="og:image:height"]',
        'meta[property="og:image:alt"]',
        'meta[property="og:image:secure_url"]',
        'meta[name="twitter:image"]',
        'meta[name="twitter:image:src"]',
      ];
      selectors.forEach((sel) => {
        const tags = Array.from(document.head.querySelectorAll(sel));
        // Se só há 1 tag (ou zero), nada a deduplicar.
        if (tags.length <= 1) return;
        // Mantém a ÚLTIMA (Helmet), neutraliza as anteriores.
        const keep = tags[tags.length - 1];
        tags.forEach((tag) => {
          if (tag === keep) return;
          const original = tag.getAttribute("content");
          if (original === "" || original == null) return;
          neutralized.push({ tag, original });
          tag.setAttribute("content", "");
        });
      });
    };

    // 4 rAFs ≈ 2 frames depois do Helmet flush (mais defensivo)
    let count = 4;
    const tick = () => {
      if (cancelled) return;
      if (count-- > 0) requestAnimationFrame(tick);
      else neutralize();
    };
    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      // Restaura o content original — safe porque setAttribute não
      // mexe na árvore do DOM (só muda um atributo).
      neutralized.forEach(({ tag, original }) => {
        try { tag.setAttribute("content", original); } catch { /* noop */ }
      });
    };
  }, [image, fullImage]);

  // Out/2026 — Dedupe de <title>, meta description e canonical. O
  // index.html (e o HTML pré-renderizado pelo Worker) já traz essas tags;
  // o Helmet cria as dele no fim do <head>. Mantemos a ÚLTIMA (Helmet) e
  // removemos as anteriores — só nós que não são geridos pelo React
  // (tags estáticas do HTML), para nunca mexer na fiber tree.
  useEffect(() => {
    const isReactNode = (n) => Object.keys(n).some((k) => k.startsWith("__react"));
    const rafs = [];
    rafs.push(requestAnimationFrame(() => {
      rafs.push(requestAnimationFrame(() => {
        ["title", 'meta[name="description"]:not([lang])', 'link[rel="canonical"]', ...(author ? ['meta[name="author"]'] : [])].forEach((sel) => {
          const tags = Array.from(document.head.querySelectorAll(sel));
          if (tags.length <= 1) return;
          const hasReact = tags.some(isReactNode);
          // Com React 19 o Helmet hoista as tags para o <head>; nesse caso
          // removemos TODAS as estáticas. Sem tag React, mantemos a última.
          const victims = hasReact ? tags.filter((t) => !isReactNode(t)) : tags.slice(0, -1);
          victims.forEach((tag) => {
            if (isReactNode(tag)) return;
            try { tag.parentNode?.removeChild(tag); } catch { /* noop */ }
          });
        });
      }));
    }));
    return () => rafs.forEach((id) => cancelAnimationFrame(id));
  }, [fullTitle, cleanDesc, fullUrl, author]);

  return (
    <Helmet
      htmlAttributes={{ lang: "pt-BR" }}
      prioritizeSeoTags
    >
      <title>{fullTitle}</title>
      <meta name="description" content={cleanDesc} />
      {/* Traduções da descrição sitewide — só quando a página usa o
          SITE_PITCH padrão. Iter189. */}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="en" content={SITE_PITCH_TRANSLATIONS.en} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="es" content={SITE_PITCH_TRANSLATIONS.es} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="it" content={SITE_PITCH_TRANSLATIONS.it} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="fr" content={SITE_PITCH_TRANSLATIONS.fr} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="zh-CN" content={SITE_PITCH_TRANSLATIONS["zh-CN"]} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="de" content={SITE_PITCH_TRANSLATIONS.de} />}
      {cleanDesc === SITE_PITCH && !noindex && <meta name="description" lang="ru" content={SITE_PITCH_TRANSLATIONS.ru} />}
      {/* Only emit keywords when the page wants to override the global
          list from index.html — avoids React Helmet creating duplicate
          tags that confuse some crawlers (Bing in particular). */}
      {kwArray.length > 0 && (
        <meta name="keywords" content={mergedKeywords} />
      )}
      {noindex && (
        <meta
          name="robots"
          content={noindexFollow ? "noindex,follow" : "noindex,nofollow"}
        />
      )}
      <link rel="canonical" href={fullUrl} />

      {/* Out/2026 — hreflang apontando para a MESMA URL foi removido
          (ruído para o Google). Alternates reais só existem nos
          episódios trilíngues da Rádio, emitidos pela própria página. */}
      {author && <meta name="author" content={author} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDesc} />
      <meta property="og:image" content={fullImage} />
      {/* Iter298 — dimensões + alt do og:image. Google Discover / News
          usam esses campos para renderizar o card grande (1200×630). */}
      {fullImage && (
        <meta property="og:image:secure_url" content={fullImage} />
      )}
      {fullImage && imageWidth && (
        <meta property="og:image:width" content={String(imageWidth)} />
      )}
      {fullImage && imageHeight && (
        <meta property="og:image:height" content={String(imageHeight)} />
      )}
      {fullImage && imageAlt && (
        <meta property="og:image:alt" content={imageAlt} />
      )}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDesc} />
      <meta name="twitter:image" content={fullImage} />
      {fullImage && imageAlt && (
        <meta name="twitter:image:alt" content={imageAlt} />
      )}
    </Helmet>
  );
}
